import { DesktopComputerIcon } from '@heroicons/react/solid';
import { getRevenues } from 'api/v2/revenues';
import { getTaxBySlugV2, getTaxes } from 'api/v2/taxes';
import { getExpenses } from 'api/v3/expenses';
import { Button } from 'components/atoms/Button';
import ContainerWithBorder from 'components/atoms/ContainerWithBorder';
import HourGlassIcon from 'components/atoms/SVG/HourGlassIcon.component';
import ExpensesIcon from 'components/atoms/SVG/sidebarIcons/ExpensesIcon.component';
import RevenuesIcon from 'components/atoms/SVG/sidebarIcons/RevenuesIcon.component';
import TaxSubmissionIcon from 'components/atoms/SVG/TaxSubmissionIcon.component';
import TopStatusIcon from 'components/atoms/SVG/TopStatusIcon.component';
import ActionListItem from 'components/molecules/ActionListItem.component';
import CustomLoader from 'components/molecules/CustomLoader.component';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useShouldRecommendSMALLBusinessPlan from 'hooks/Growth/useShouldRecommendSMALLBusinessPlan';
import { useSubscriptionPlan } from 'hooks/Subscriptions';
import useStripeSubscription from 'hooks/Subscriptions/useStripeSubscription';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import { motion } from 'motion/react';
import { useContext, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { UserCacheKeys } from 'types/cacheKeys.types';
import { RevenueType } from 'types/revenues.types';
import { SubscriptionPlanNameEnum, type UserCountry } from 'types/users.types';
import { getTracingEvents } from 'utils/amplitude';
import {
  WEB_TRIALNUDGE_CLICKEDADDFIVEEXPENSESPERMONTH,
  WEB_TRIALNUDGE_CLICKEDCOMPAREPLANS,
  WEB_TRIALNUDGE_CLICKEDPERSONALIZEINVOICES,
  WEB_TRIALNUDGE_CLICKEDTAXGUARANTEE,
  WEB_TRIALNUDGE_CLICKEDTAXSUBMISSION,
  WEB_TRIALNUDGE_CLICKEDUPGRADENOW,
} from 'utils/amplitude/events/user.amplitude';
import layoutContext from '../Layout/layoutContext';
import NavigationItem from './NavigationItem.component';
import { SidebarNavigationItemTypesEnum } from './types';

const tracingEvents = getTracingEvents({
  onClickPersonalizeInvoices: WEB_TRIALNUDGE_CLICKEDPERSONALIZEINVOICES,
  onClickAddExpensesPerMonth: WEB_TRIALNUDGE_CLICKEDADDFIVEEXPENSESPERMONTH,
  onClickTaxSubmission: WEB_TRIALNUDGE_CLICKEDTAXSUBMISSION,
  onClickTaxGuarantee: WEB_TRIALNUDGE_CLICKEDTAXGUARANTEE,
});

const FeaturesListContent: FC<{
  showPROFeatures: boolean;
  setShowPROFeatures: (state: boolean) => void;
  numberOfDaysLeft: number;
}> = ({ showPROFeatures, setShowPROFeatures, numberOfDaysLeft }) => {
  const layout = useContext(layoutContext);

  const { data: user } = useGetCurrentCustomerData();

  const { subscribe, isLoading } = useStripeSubscription();

  const navigate = useCustomNavigate();

  const shouldRecommendSMALLPlan = useShouldRecommendSMALLBusinessPlan();

  const recommendedPlanName = shouldRecommendSMALLPlan ? 'SMALL' : 'PRO';

  const revenuesQuery = useQuery(
    UserCacheKeys.TrialNudgeRevenues,
    () =>
      getRevenues({
        page: 1,
        perPage: 999,
        types: [RevenueType.invoice],
        isInvoice: true,
        fields: ['type', 'fileType'],
      }),
    {
      select: (data) =>
        data.data?.filter((invoice) => invoice.fileType === 'generated'),
      staleTime: 0,
    },
  );

  const expensesQuery = useQuery(
    UserCacheKeys.TrialNudgeExpenses,
    () =>
      getExpenses({
        page: 1,
        perPage: 100,
        fields: '_id',
      }),
    {
      select: (data) => {
        const past30DaysExpenses = data.data?.filter((expense) => {
          const diff = dayjs().diff(dayjs(expense?.created), 'day');

          return diff <= 30;
        });

        return past30DaysExpenses;
      },
      staleTime: 0,
    },
  );

  const taxesQuery = useQuery(
    UserCacheKeys.TrialNudgeTaxes,
    () =>
      getTaxes({
        types: 'vat_submission,intracom_listing,income_tax_submission,EUR',
        statuses: 'done',
      }),
    {
      staleTime: 0,
    },
  );

  const submittedTaxesDetailsQuery = useQuery(
    UserCacheKeys.TrialNudgeSubmittedTaxes,
    () =>
      Promise.all(
        (taxesQuery.data as any)?.data?.map((tax: any) =>
          getTaxBySlugV2(
            user?.country as UserCountry,
            tax.period as any,
            tax.type as any,
          ),
        ) as any,
      ),
    {
      enabled: ((taxesQuery.data as any)?.data?.length || 0) > 0,
      staleTime: 0,
    },
  );

  const { t } = useTranslation();

  return (
    <motion.div
      className="fixed top-7 shadow-filters rounded-lg overflow-hidden min-w-[400px] z-50"
      style={{ left: layout.sidebarArea - 40 }}
      initial={{ opacity: 0 }}
      animate={showPROFeatures ? { opacity: 1, zIndex: 34234 } : {}}
    >
      <ContainerWithBorder innerWrapperClassName="px-5 py-4">
        {revenuesQuery.isLoading ||
        expensesQuery.isLoading ||
        taxesQuery.isLoading ||
        submittedTaxesDetailsQuery.isLoading ? (
          <div className="min-h-[400px] flex items-center justify-center">
            <CustomLoader />
          </div>
        ) : (
          <>
            <h1 className="text-primary font-bold">
              {t('trial_nudge.title', {
                numberOfDaysLeft,
                recommendedPlanName,
              })}
            </h1>

            <p>{t('trial_nudge.subtitle', { recommendedPlanName })}</p>

            <ul className="mt-5">
              <ActionListItem
                label="Accountable Web"
                isDone={true}
                Icon={DesktopComputerIcon}
              />

              <ActionListItem
                label={t('trial_nudge.personalize_invoices')}
                isDone={!!revenuesQuery.data?.length}
                onClick={() => {
                  tracingEvents.onClickPersonalizeInvoices();

                  setShowPROFeatures(false);
                  navigate('/revenues/create');
                }}
                Icon={RevenuesIcon}
              />

              <ActionListItem
                label={t('trial_nudge.add_expenses')}
                isDone={(expensesQuery.data?.length || 0) > 4}
                onClick={() => {
                  tracingEvents.onClickAddExpensesPerMonth();

                  setShowPROFeatures(false);
                  navigate('/expenses');
                }}
                Icon={ExpensesIcon}
              />

              <ActionListItem
                label={t('trial_nudge.tax_submssion')}
                isDone={((taxesQuery.data as any)?.data?.length || 0) > 0}
                onClick={() => {
                  tracingEvents.onClickTaxSubmission();

                  setShowPROFeatures(false);
                  navigate('/taxes');
                }}
                Icon={TaxSubmissionIcon}
              />

              <ActionListItem
                label={t('trial_nudge.tax_guarantee')}
                isDone={
                  !!submittedTaxesDetailsQuery.data?.find(
                    (tax) => tax.isGuaranteed,
                  )
                }
                onClick={() => {
                  tracingEvents.onClickTaxGuarantee();

                  setShowPROFeatures(false);
                  navigate('/taxes');
                }}
                Icon={TopStatusIcon}
              />
            </ul>

            <div className="flex flex-col w-full space-y-2 mt-5">
              <Button
                loading={isLoading}
                tracingEvent={WEB_TRIALNUDGE_CLICKEDUPGRADENOW}
                onClick={() =>
                  subscribe(
                    shouldRecommendSMALLPlan
                      ? SubscriptionPlanNameEnum.small12
                      : SubscriptionPlanNameEnum.pro12,
                  )
                }
              >
                {t('trial_nudge.upgrade_cta')}
              </Button>

              <Button
                structure="text"
                tracingEvent={WEB_TRIALNUDGE_CLICKEDCOMPAREPLANS}
                onClick={() => navigate('/subscriptions')}
              >
                {t('trial_nudge.compare_cta', { recommendedPlanName })}
              </Button>
            </div>
          </>
        )}
      </ContainerWithBorder>
    </motion.div>
  );
};

const TrialFeaturesList: FC = () => {
  const { t } = useTranslation();

  const [showPROFeatures, setShowPROFeatures] = useState(false);

  const subscriptionPlan = useSubscriptionPlan();

  const trialExpirationDate = dayjs(subscriptionPlan?.end);

  const numberOfDaysLeft = trialExpirationDate.diff(dayjs(), 'd') || 1;

  return (
    <div className="relative w-full">
      <div
        onMouseEnter={() => setShowPROFeatures(true)}
        onMouseLeave={() => setShowPROFeatures(false)}
      >
        <NavigationItem
          item={{
            Icon: HourGlassIcon,
            type: SidebarNavigationItemTypesEnum.taxes,
            title: t('trial_nudge.title.short', { numberOfDaysLeft }),
          }}
          active
        />

        {showPROFeatures ? (
          <FeaturesListContent
            showPROFeatures={showPROFeatures}
            setShowPROFeatures={setShowPROFeatures}
            numberOfDaysLeft={numberOfDaysLeft}
          />
        ) : null}
      </div>
    </div>
  );
};

export default TrialFeaturesList;
